import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  OnInit,
  output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Button } from 'primeng/button';
import { InputNumber } from 'primeng/inputnumber';
import { FormControlComponent, PrimengButtonDirective, WarningComponent } from '@web/shared/ui';
import {
  SummernoteComponent,
  SummernoteImageUploadFn,
} from '@web/shared/external';
import { Nillable, valueOrDefault } from '@web/utils';
import {
  AdminBoxSaleDetailsForm,
  AdminBoxSaleDetailsFormGroup,
  AdminBoxSaleGroups,
} from '../../models/admin-box-sale-details-form';
import { AdminBoxSaleGroupsControlComponent } from '../admin-box-sale-groups-control/admin-box-sale-groups-control.component';

@Component({
  selector: 'app-admin-box-sale-details-form',
  imports: [
    AdminBoxSaleGroupsControlComponent,
    Button,
    CommonModule,
    FormControlComponent,
    InputNumber,
    PrimengButtonDirective,
    ReactiveFormsModule,
    SummernoteComponent,
    WarningComponent,
  ],
  templateUrl: './admin-box-sale-details-form.component.html',
  styleUrl: './admin-box-sale-details-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminBoxSaleDetailsFormComponent implements OnInit {
  private readonly builder = inject(FormBuilder);

  public readonly data = input<Nillable<AdminBoxSaleDetailsForm>>();
  public readonly uploadFn = input.required<SummernoteImageUploadFn>();
  public readonly submitClickEvent = output<AdminBoxSaleDetailsForm>();

  protected readonly form = this.buildForm();
  protected readonly valueOrDefault = valueOrDefault;

  public ngOnInit(): void {
    this.fillByData();
  }

  public handleSubmitClick(): void {
    this.submitClickEvent.emit(this.toData());
  }

  protected canBeGroupsEdited(): boolean {
    const data = this.data();

    return !!data && data.canBeGroupsEdited;
  }

  private toData(): AdminBoxSaleDetailsForm {
    const data = this.form.getRawValue();

    return new AdminBoxSaleDetailsForm({
      canBeGroupsEdited: this.canBeGroupsEdited(),
      description: data.description,
      groups: data.groups,
      validityHours: data.validityHours,
    });
  }

  private buildForm(): AdminBoxSaleDetailsFormGroup {
    return this.builder.group({
      description: this.builder.control<Nillable<string>>(null, [
        Validators.maxLength(AdminBoxSaleDetailsForm.DescriptionMaxLength),
      ]),
      groups: this.builder.control<Nillable<AdminBoxSaleGroups>>(null),
      validityHours: this.builder.nonNullable.control(
        AdminBoxSaleDetailsForm.DefaultValidityHours,
        [Validators.required, Validators.min(1)],
      ),
    });
  }

  private fillByData(): void {
    const data = this.data();

    this.form.patchValue({
      description: data?.description,
      groups: data?.groups,
      validityHours:
        data?.validityHours ?? AdminBoxSaleDetailsForm.DefaultValidityHours,
    });
  }
}
